import { useEffect } from "react";
import { t } from "@excalidraw/excalidraw/i18n";

const SITE_URL = "https://record.ezpic.fun";
const OG_IMAGE = `${SITE_URL}/og-image-3.png`;

const updateMetaTag = (
  selector: string,
  content: string,
  attrName = "content",
) => {
  const el = document.querySelector(selector);
  if (el) {
    el.setAttribute(attrName, content);
  }
};

const updateJsonLd = (langCode: string) => {
  const script = document.querySelector('script[type="application/ld+json"]');
  if (!script) {
    return;
  }
  try {
    const data = JSON.parse(script.textContent || "{}");
    data.inLanguage = langCode;
    script.textContent = JSON.stringify(data);
  } catch {
    // ignore parse errors on JSON-LD
  }
};

/**
 * Updates document meta tags (title, OG, Twitter, canonical, JSON-LD)
 * based on the current i18n language. Should be called in the LandingPage component.
 */
export const useDocumentMeta = () => {
  useEffect(() => {
    const langCode = document.documentElement.lang || "en";
    const title = t("landing.meta.title");
    const description = t("landing.meta.description");
    const ogTitle = t("landing.meta.ogTitle");
    const ogDescription = t("landing.meta.ogDescription");

    // Document title
    document.title = title;

    // Primary meta tags
    updateMetaTag('meta[name="title"]', title);
    updateMetaTag('meta[name="description"]', description);

    // Open Graph
    updateMetaTag('meta[property="og:title"]', ogTitle);
    updateMetaTag('meta[property="og:description"]', ogDescription);
    updateMetaTag('meta[property="og:image"]', OG_IMAGE);

    // Twitter
    updateMetaTag('meta[property="twitter:title"]', ogTitle);
    updateMetaTag('meta[property="twitter:description"]', ogDescription);
    updateMetaTag('meta[property="twitter:image"]', OG_IMAGE);

    // Canonical URL — include ?lang= for non-English
    const isEnglish = langCode === "en" || langCode.startsWith("en-");
    const canonicalUrl = isEnglish
      ? SITE_URL
      : `${SITE_URL}/?lang=${langCode}`;
    updateMetaTag('link[rel="canonical"]', canonicalUrl, "href");
    updateMetaTag('meta[property="og:url"]', canonicalUrl);
    updateMetaTag('meta[property="twitter:url"]', canonicalUrl);

    // JSON-LD inLanguage
    updateJsonLd(langCode);
  }, []);
};
